import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUpdateCompartmentVolumeMutation,
  useGetGoodProfileListQuery,
  useGetTrailerDetailsQuery,
  AssetStatus,
} from "../../../graphql/generated";
import { maxFill, CompartmentCellData } from "../../common/CompartmentGraphic/CompartmentGraphic";

export type CompartmentVolumeEditModalProps = {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should be closed */
  onClose: () => void;
  /** The trailer id */
  trailerId: string;
  /** The compartment being edited */
  compartment: CompartmentCellData | null;
  /** Current volume data for the compartment (if any) */
  currentCommodityId?: string | null;
  currentQuantity?: number | null;
};

type FormState = {
  commodityId: string;
  quantity: string;
};

type ValidationErrors = {
  commodityId?: string;
  quantity?: string;
};

function validate(
  values: FormState,
  compartment: CompartmentCellData
): ValidationErrors {
  const errors: ValidationErrors = {};

  const qty = parseFloat(values.quantity);
  const hasQty = values.quantity !== "" && !isNaN(qty);

  // Commodity required only when quantity > 0
  if (!values.commodityId && hasQty && qty > 0) {
    errors.commodityId = "A commodity must be selected.";
  }

  if (!hasQty) {
    errors.quantity = "Quantity is required.";
  } else if (qty < 0) {
    errors.quantity = "Quantity cannot be negative.";
  } else {
    const max = maxFill(compartment);
    if (qty > max) {
      errors.quantity = `Quantity cannot exceed the Max Fill (${max} ${compartment.unit}).`;
    }
  }

  return errors;
}

export default function CompartmentVolumeEditModal({
  open,
  onClose,
  trailerId,
  compartment,
  currentCommodityId,
  currentQuantity,
}: CompartmentVolumeEditModalProps) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>({
    commodityId: currentCommodityId ?? "",
    quantity: currentQuantity != null ? String(currentQuantity) : "",
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [serverError, setServerError] = useState<Error | null>(null);

  const updateMutation = useUpdateCompartmentVolumeMutation();
  const goodProfilesQuery = useGetGoodProfileListQuery({ includeInactive: true });
  const goodProfiles = (goodProfilesQuery.data?.goodProfiles.data ?? []).filter(
    (profile) =>
      profile.status !== AssetStatus.Inactive ||
      profile._id === currentCommodityId
  );

  // Re-initialize form when the modal opens for a new compartment
  useEffect(() => {
    if (open) {
      setForm({
        commodityId: currentCommodityId ?? "",
        quantity: currentQuantity != null ? String(currentQuantity) : "",
      });
      setValidationErrors({});
      setServerError(null);
    }
  }, [open, currentCommodityId, currentQuantity]);

  const handleClose = () => {
    if (!updateMutation.isLoading) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!compartment) return;

    const errors = validate(form, compartment);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setServerError(null);

    try {
      await updateMutation.mutateAsync({
        trailerId,
        compartmentId: compartment.identifier,
        commodityId: form.commodityId,
        quantity: parseFloat(form.quantity),
      });

      // Invalidate the trailer details query to refresh the UI
      await queryClient.invalidateQueries(
        useGetTrailerDetailsQuery.getKey({ id: trailerId })
      );

      onClose();
    } catch (error) {
      setServerError(error as Error);
    }
  };

  if (!compartment) return null;

  const maxFillValue = maxFill(compartment);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        data-testid="compartment-volume-edit-modal"
      >
        <DialogTitle>
          Edit Compartment Volume — {compartment.identifier}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Max Fill: {maxFillValue} {compartment.unit}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Commodity selector */}
            <FormControl
              fullWidth
              error={!!validationErrors.commodityId}
              data-testid="commodity-select-control"
            >
              <InputLabel id="commodity-label">Commodity</InputLabel>
              <Select
                labelId="commodity-label"
                label="Commodity"
                value={form.commodityId}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    commodityId: e.target.value,
                  }));
                  setValidationErrors((prev) => ({
                    ...prev,
                    commodityId: undefined,
                  }));
                }}
                inputProps={{ "data-testid": "commodity-select" }}
                data-testid="commodity-select-dropdown"
              >
                {goodProfiles.map((profile) => (
                  <MenuItem
                    key={profile._id}
                    value={profile._id}
                    data-testid={`commodity-option-${profile._id}`}
                  >
                    {profile.label}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.commodityId && (
                <FormHelperText data-testid="commodity-error">
                  {validationErrors.commodityId}
                </FormHelperText>
              )}
            </FormControl>

            {/* Quantity input */}
            <TextField
              label="Quantity"
              type="number"
              fullWidth
              value={form.quantity}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, quantity: e.target.value }));
                setValidationErrors((prev) => ({
                  ...prev,
                  quantity: undefined,
                }));
              }}
              error={!!validationErrors.quantity}
              helperText={
                validationErrors.quantity ||
                `Enter a value between 0 and ${maxFillValue} ${compartment.unit}`
              }
              inputProps={{
                min: 0,
                max: maxFillValue,
                step: "any",
                "data-testid": "quantity-input",
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={updateMutation.isLoading}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            loading={updateMutation.isLoading}
            data-testid="save-button"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Server error dialog — shown when the save operation fails */}
      <Dialog
        open={!!serverError}
        onClose={() => setServerError(null)}
        maxWidth="xs"
        data-testid="server-error-dialog"
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            {serverError?.message ||
              "An error occurred while saving. Please try again."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setServerError(null)}
            variant="contained"
            data-testid="error-dialog-close-button"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
